import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { ApiClient } from "./api";
import type { Session } from "./session";

interface DeskContextValue {
  api: ApiClient;
  session: Session;
  /** Forget the current session (used by Settings → Unpair). */
  signOut: () => void;
}

const DeskContext = createContext<DeskContextValue | null>(null);

export function DeskProvider({ value, children }: { value: DeskContextValue; children: ReactNode }) {
  return <DeskContext.Provider value={value}>{children}</DeskContext.Provider>;
}

export function useDesk(): DeskContextValue {
  const v = useContext(DeskContext);
  if (!v) throw new Error("useDesk outside DeskProvider");
  return v;
}

export function useApi(): ApiClient {
  return useDesk().api;
}

/** Re-renders once a minute so "today" and relative labels roll over without a reload. */
export function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    const onVis = () => document.visibilityState === "visible" && setNow(Date.now());
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
  return now;
}
